import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { CheckCircle, ExternalLink, FileText, Mail, Scale } from 'lucide-react';

export default function Step7Agreements({ formData, setFormData, agreements, setAgreements }) {
  return (
          <div className="space-y-6">
            {/* Contact Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Contact & Settings</CardTitle>
                <CardDescription>Optional details to complete your club setup</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="email">Club Contact Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="contact@yourclub.com"
                  />
                </div>
                
                <div>
                  <Label htmlFor="website">Website URL</Label>
                  <Input
                    id="website"
                    type="url"
                    value={formData.website_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, website_url: e.target.value }))}
                    placeholder="https://yourclub.com"
                  />
                </div>
                
                <div className="flex items-center gap-3 p-4 rounded-lg border">
                  <Checkbox
                    id="free-members"
                    checked={formData.allows_free_members}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, allows_free_members: checked }))}
                  />
                  <div>
                    <Label htmlFor="free-members" className="font-medium cursor-pointer">Allow Free Members</Label>
                    <p className="text-sm text-muted-foreground">Free users can view public content (recommended)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Legal Agreements */}
            <Card className="border-amber-200">
              <CardHeader className="bg-amber-50/50">
                <CardTitle className="flex items-center gap-2">
                  <Scale className="w-5 h-5 text-amber-700" />
                  Club Agreements
                </CardTitle>
                <CardDescription>
                  Please review and accept the following agreements to create your club
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                {/* Club Bylaws Agreement */}
                <div className={`p-4 rounded-lg border-2 transition-all ${
                  formData.bylaws_accepted ? 'border-green-500 bg-green-50/50' : 'border-border'
                }`}>
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="bylaws"
                      checked={formData.bylaws_accepted}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, bylaws_accepted: checked }))}
                      className="mt-1"
                      data-testid="bylaws-checkbox"
                    />
                    <div className="flex-1">
                      <Label htmlFor="bylaws" className="font-medium cursor-pointer flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        I accept the Standard Club Bylaws *
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        The bylaws establish governance rules, membership policies, and operational guidelines for your club.
                      </p>
                      <Link 
                        to="/about/bylaws" 
                        target="_blank"
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-2"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Read the Club Bylaws
                      </Link>
                    </div>
                  </div>
                </div>

                {/* VBB Service Agreement */}
                <div className={`p-4 rounded-lg border-2 transition-all ${
                  formData.service_agreement_accepted ? 'border-green-500 bg-green-50/50' : 'border-border'
                }`}>
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="service-agreement"
                      checked={formData.service_agreement_accepted}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, service_agreement_accepted: checked }))}
                      className="mt-1"
                      data-testid="service-agreement-checkbox"
                    />
                    <div className="flex-1">
                      <Label htmlFor="service-agreement" className="font-medium cursor-pointer flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        I accept the VBB Service Agreement *
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        The service agreement covers technology services, revenue sharing (10%), support, and hosting provided by Very Big Box LLC.
                      </p>
                      <Link 
                        to="/about/service-agreement" 
                        target="_blank"
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-2"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Read the VBB Service Agreement
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Request Modification Option */}
                <div className="p-4 rounded-lg bg-muted/50">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="request-modification"
                      checked={formData.request_modification}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, request_modification: checked }))}
                      className="mt-1"
                      data-testid="modification-checkbox"
                    />
                    <div className="flex-1">
                      <Label htmlFor="request-modification" className="font-medium cursor-pointer flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Request Agreement Modifications
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        If you need modifications to the standard agreements, you may accept them as written now and submit a request for changes.
                        Our team will contact you to discuss your needs.
                      </p>
                    </div>
                  </div>
                  
                  {formData.request_modification && (
                    <div className="mt-4 ml-6">
                      <Label htmlFor="modification-notes">What modifications would you like to request?</Label>
                      <Textarea
                        id="modification-notes"
                        value={formData.modification_notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, modification_notes: e.target.value }))}
                        placeholder="Describe the changes you'd like to request..."
                        rows={3}
                        className="mt-2"
                        data-testid="modification-notes-input"
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        Your club will be created with the standard agreements. We'll review your modification request and follow up via email.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Summary */}
            <Card className="bg-primary/5 border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  Review Your Club
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Club Name:</span>
                    <p className="font-medium">{formData.name}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">URL:</span>
                    <p className="font-medium">/{formData.slug}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Pet Types:</span>
                    <p className="font-medium">{formData.pet_types.join(', ') || 'None selected'}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Club Type:</span>
                    <p className="font-medium capitalize">{formData.club_type}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Location:</span>
                    <p className="font-medium">{formData.region || formData.state || formData.country}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Color Theme:</span>
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: formData.primary_color }} />
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: formData.secondary_color }} />
                        <div className="w-4 h-4 rounded border" style={{ backgroundColor: formData.accent_color }} />
                      </div>
                      <span className="font-medium">{formData.selectedPreset}</span>
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Features:</span>
                    <p className="font-medium">{formData.features.length} enabled</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Agreements:</span>
                    <p className="font-medium">
                      {formData.bylaws_accepted && formData.service_agreement_accepted 
                        ? '✓ Both accepted' 
                        : 'Pending acceptance'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
  );
}
